
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";

import {
  formatDateFR,
  getArticleName,
  getPaymentKindLabel,
  getUserFullName,
  safeArray,
  toAmount,
} from "./paymentsDashboardUtils";

const HEADERS = [
  "Date",
  "Utilisateur",
  "Email",
  "Article",
  "Type d'achat",
  "Montant (€)",
  "Statut",
  "Moyen de règlement",
  "Visibilité",
];

function getRows(payments) {
  return safeArray(payments).map((payment) => ({
    Date: formatDateFR(payment.date_achat),
    Utilisateur: getUserFullName(payment.User),
    Email: payment.User?.email || "",
    Article: getArticleName(payment),
    "Type d'achat": getPaymentKindLabel(payment),
    "Montant (€)": toAmount(payment.prix_total),
    Statut: payment.statut_reglement || "",
    "Moyen de règlement": payment.type_reglement || "",
    Visibilité: payment.autorisation_achat
      ? "Visible"
      : "Masqué",
  }));
}

function getFileName(season, extension) {
  const date = new Date().toLocaleDateString("fr-CA");

  const safeSeason = String(season || "toutes-saisons")
    .replace(/[^a-zA-Z0-9-]/g, "_");

  return `paiements_${safeSeason}_${date}.${extension}`;
}

function getSummary(payments) {
  const rows = safeArray(payments);

  return {
    count: rows.length,
    total: rows.reduce(
      (sum, payment) => sum + toAmount(payment.prix_total),
      0
    ),
    paid: rows
      .filter((payment) => payment.statut_reglement === "Réglé")
      .reduce(
        (sum, payment) => sum + toAmount(payment.prix_total),
        0
      ),
  };
}

// =====================================================
// EXPORT EXCEL
// =====================================================

export function exportPaymentsExcel(payments, season) {
  const rows = getRows(payments);
  const summary = getSummary(payments);

  if (!rows.length) {
    throw new Error("Aucun paiement à exporter.");
  }

  const worksheet = XLSX.utils.json_to_sheet(rows, {
    header: HEADERS,
  });

  worksheet["!cols"] = [
    { wch: 14 },
    { wch: 28 },
    { wch: 35 },
    { wch: 35 },
    { wch: 23 },
    { wch: 16 },
    { wch: 24 },
    { wch: 23 },
    { wch: 16 },
  ];

  const summaryStart = rows.length + 3;

  XLSX.utils.sheet_add_aoa(
    worksheet,
    [
      ["RÉCAPITULATIF"],
      ["Nombre de paiements", summary.count],
      ["Montant total (€)", summary.total],
      ["Montant réglé (€)", summary.paid],
    ],
    { origin: `A${summaryStart}` }
  );

  // Montants numériques exploitables dans Excel.
  for (let row = 2; row <= rows.length + 1; row++) {
    const cell = worksheet[`F${row}`];

    if (cell) {
      cell.z = '#,##0.00 "€"';
    }
  }

  const totalCell = worksheet[`B${summaryStart + 2}`];
  const paidCell = worksheet[`B${summaryStart + 3}`];

  if (totalCell) totalCell.z = '#,##0.00 "€"';
  if (paidCell) paidCell.z = '#,##0.00 "€"';

  worksheet["!autofilter"] = {
    ref: `A1:I${rows.length + 1}`,
  };

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Paiements"
  );

  XLSX.writeFile(
    workbook,
    getFileName(season, "xlsx")
  );
}

// =====================================================
// EXPORT PDF
// Sans dépendance jspdf-autotable
// =====================================================

export function exportPaymentsPdf(payments, season) {
  const rows = getRows(payments);
  const summary = getSummary(payments);

  if (!rows.length) {
    throw new Error("Aucun paiement à exporter.");
  }

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const margin = 10;
  const rowHeight = 8;

  // Colonnes compactes pour tenir en A4 paysage.
  const columns = [
    { key: "Date", label: "Date", width: 22 },
    { key: "Utilisateur", label: "Utilisateur", width: 37 },
    { key: "Email", label: "Email", width: 48 },
    { key: "Article", label: "Article", width: 42 },
    { key: "Montant (€)", label: "Montant", width: 24 },
    { key: "Statut", label: "Statut", width: 38 },
    {
      key: "Moyen de règlement",
      label: "Règlement",
      width: 28,
    },
    { key: "Visibilité", label: "Visible", width: 18 },
  ];

  const tableWidth = columns.reduce(
    (sum, column) => sum + column.width,
    0
  );

  function drawPageHeader() {
    pdf.setFillColor(1, 62, 142);
    pdf.rect(0, 0, pageWidth, 23, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(255, 255, 255);

    pdf.text("EXPORT DES PAIEMENTS", margin, 11);

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");

    pdf.text(
      `Saison : ${season || "Toutes"}  |  ${rows.length} paiement(s)`,
      margin,
      18
    );
  }

  function drawTableHeader(y) {
    pdf.setFillColor(225, 232, 242);
    pdf.rect(margin, y, tableWidth, rowHeight, "F");

    pdf.setTextColor(25, 40, 65);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);

    let x = margin;

    columns.forEach((column) => {
      pdf.text(column.label, x + 2, y + 5.2);
      x += column.width;
    });

    return y + rowHeight;
  }

  function drawFooter() {
    const pageNumber = pdf.internal.getCurrentPageInfo().pageNumber;

    pdf.setTextColor(110, 120, 135);
    pdf.setFontSize(8);

    pdf.text(
      `MonAppliClub - Page ${pageNumber}`,
      pageWidth - margin,
      pageHeight - 6,
      { align: "right" }
    );
  }

  drawPageHeader();

  let y = drawTableHeader(29);

  rows.forEach((row, index) => {
    if (y + rowHeight > pageHeight - 16) {
      drawFooter();
      pdf.addPage();
      drawPageHeader();
      y = drawTableHeader(29);
    }

    if (index % 2 === 0) {
      pdf.setFillColor(246, 248, 252);
      pdf.rect(margin, y, tableWidth, rowHeight, "F");
    }

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(35, 45, 60);

    let x = margin;

    columns.forEach((column) => {
      let value = row[column.key];

      if (column.key === "Montant (€)") {
        value = `${Number(value).toFixed(2)} EUR`;
      }

      // Évite le débordement des cellules.
      const maxWidth = column.width - 4;

      const text = pdf.splitTextToSize(
        String(value ?? ""),
        maxWidth
      )[0] || "";

      pdf.text(text, x + 2, y + 5.2);
      x += column.width;
    });

    y += rowHeight;
  });

  if (y + 28 > pageHeight - 15) {
    drawFooter();
    pdf.addPage();
    drawPageHeader();
    y = 30;
  }

  pdf.setFillColor(235, 242, 250);
  pdf.roundedRect(margin, y + 5, 130, 23, 2, 2, "F");

  pdf.setTextColor(20, 40, 70);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);

  pdf.text(
    `Total des paiements : ${summary.count}`,
    margin + 4,
    y + 12
  );

  pdf.text(
    `Montant total : ${summary.total.toFixed(2)} EUR`,
    margin + 4,
    y + 18
  );

  pdf.text(
    `Montant réglé : ${summary.paid.toFixed(2)} EUR`,
    margin + 4,
    y + 24
  );

  drawFooter();

  pdf.save(getFileName(season, "pdf"));
}
